import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";
import { readBootstrapState } from "../../lib/bootstrap-state.js";
import { readStatusBlob, type StatusDoc } from "../../lib/bootstrap-status.js";
import { getAciState } from "../../lib/bootstrap-aci-state.js";

export class BootstrapStatusCommand extends M8tCommand {
  static paths = [["bootstrap", "status"]];
  static usage = Command.Usage({
    description: "Show the cloud installer's live status (phase, progress, result).",
    details: "Reads the durable status blob written by the installer. --watch polls until the install reaches done or failed.",
    examples: [["One read", "$0 bootstrap status"], ["Watch to completion", "$0 bootstrap status --watch"]],
  });

  watch = Option.Boolean("--watch", false);
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const state = await readBootstrapState();
    if (!state) {
      throw new LocalCliError({
        code: "BOOTSTRAP_NO_STATE",
        message: "No bootstrap is in flight (no ~/.m8t-stack/bootstrap.json).",
        hint: "Run 'm8t bootstrap launch --location <region>' first.",
      });
    }
    // Standard convention: auto → pretty on a TTY, json when piped/redirected
    // (matches every other m8t command). No bespoke default.
    const mode = resolveOutputMode(
      typeof this.output === "string" ? (this.output as "pretty" | "json" | "auto") : undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
    const watch = this.watch === true;

    for (;;) {
      let doc: StatusDoc;
      try {
        doc = await readStatusBlob({ saName: state.statusSaName, resourceGroup: state.resourceGroup, subscriptionId: state.subscriptionId });
      } catch (e) {
        // The blob doesn't exist until the installer's phase 1 (status storage),
        // which lands a minute or so after the ACI cold-starts. In --watch we
        // tolerate that startup window and keep polling; a single read still errors.
        if (watch && e instanceof LocalCliError && e.code === "BOOTSTRAP_STATUS_UNREADABLE") {
          if (mode === "pretty") this.context.stderr.write(`  ${colors.dim("waiting for the installer to start…")}\n`);
          await sleep(10_000);
          continue;
        }
        throw e;
      }
      if (!watch || doc.status !== "running") {
        if (mode === "json") { this.context.stdout.write(renderJson(doc) + "\n"); }
        else { this.context.stdout.write(formatStatus(doc)); }
        return doc.status === "failed" ? 1 : 0;
      }
      if (mode === "pretty") this.context.stderr.write(`  ${colors.dim(`${doc.phase} (${String(doc.phaseIndex)}/${String(doc.phaseTotal)}) ${doc.detail ?? ""}`)}\n`);
      const aci = await getAciState({ aciName: state.aciName, resourceGroup: state.resourceGroup, subscriptionId: state.subscriptionId }).catch(() => null);
      if (aci?.terminated) {
        this.context.stderr.write(
          `  ${colors.error("✗")} the installer container has terminated (exit ${String(aci.exitCode ?? "?")}) but the status is still '${doc.phase}'.\n` +
          `  ${colors.hint("inspect:")} az container logs -n ${state.aciName} -g ${state.resourceGroup}\n` +
          `  ${colors.hint("reap:")} m8t bootstrap reap --force\n`,
        );
        return 1;
      }
      await sleep(10_000);
    }
  }
}

function formatStatus(d: StatusDoc): string {
  const head = `${d.status.toUpperCase()} — ${d.phase} (${String(d.phaseIndex)}/${String(d.phaseTotal)})${d.detail ? ` — ${d.detail}` : ""}\n`;
  if (d.status === "done" && d.result) {
    return colors.success("✓ ") + head +
      `  gateway:  ${d.result.gatewayUrl ?? "-"}\n` +
      `  foundry:  ${d.result.foundryEndpoint ?? "-"}\n` +
      `  ${colors.hint("next:")} m8t bootstrap reap && m8t bootstrap finish\n`;
  }
  if (d.status === "failed" && d.error) {
    return colors.error("✗ ") + head + `  ${colors.error(`error in ${d.error.phase}: ${d.error.message}`)}\n  ${colors.hint("inspect:")} az container logs\n`;
  }
  return head;
}
